import { RefreshCw } from "lucide-react";
import type { ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { Forbidden } from "@/app/errors";
import { AppLoading } from "@/app/shell/PageLoading";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";
import { errorMessage } from "@/lib/api";
import { hasPermissions, type PermissionCheck } from "@/lib/permissions";
import { useAuth } from "./session";

/**
 * Route guards. They only decide what to RENDER — the API enforces the same
 * rules on every request, so a guard is never the security boundary.
 */

function SessionUnavailable({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-5 bg-canvas-glow px-4 text-center">
      <Logo className="h-8" title="lean2automate AMS" />
      <div className="max-w-md">
        <h1 className="font-display text-xl font-semibold tracking-tight">We can't confirm your session</h1>
        <p className="mt-2 text-base text-fg-muted">{errorMessage(error, "The server is not reachable right now.")}</p>
      </div>
      <Button leftIcon={<RefreshCw />} onClick={onRetry}>
        Try again
      </Button>
    </main>
  );
}

/** Requires a signed-in staff session; otherwise redirects to /login?next=…. */
export function RequireAuth() {
  const { status, refresh, error } = useAuth();
  const location = useLocation();

  if (status === "loading") return <AppLoading />;
  if (status === "error") return <SessionUnavailable error={error} onRetry={() => void refresh()} />;
  if (status === "unauthenticated") {
    const next = `${location.pathname}${location.search}`;
    const search = next && next !== "/" ? `?next=${encodeURIComponent(next)}` : "";
    return <Navigate to={`/login${search}`} replace />;
  }
  return <Outlet />;
}

/** Login / signup / password pages: a signed-in user is sent on to the app. */
export function RequireGuest() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <AppLoading label="Checking your session" />;
  if (status === "authenticated") {
    const next = new URLSearchParams(location.search).get("next");
    return <Navigate to={next && next.startsWith("/") ? next : "/"} replace />;
  }
  return <Outlet />;
}

/**
 * Permission gate. Use as a layout route element…
 *   { element: <RequirePermission perm="invoices:read" />, children: [...] }
 * …or inline around a section.
 */
export function RequirePermission({
  perm,
  mode = "all",
  what,
  children,
}: {
  perm: PermissionCheck;
  mode?: "all" | "any";
  /** Name used in the message, e.g. "invoicing". */
  what?: string;
  children?: ReactNode;
}) {
  const { permissions, status } = useAuth();
  if (status === "loading") return <AppLoading />;
  if (!hasPermissions(permissions, perm, mode)) return <Forbidden {...(what ? { what } : {})} />;
  return <>{children ?? <Outlet />}</>;
}
